import Vue from "vue";
import VueApollo from "vue-apollo";
import { ApolloLink } from "apollo-link";

// when using module import TS is complaining about missing definition files...
const { createApolloClient, restartWebsockets } = require("vue-cli-plugin-apollo/graphql-client");
import { SubscriptionClient } from "subscriptions-transport-ws";
import { ApolloClient } from "apollo-client";
import { InMemoryCache } from "apollo-cache-inmemory";
import { Scope } from "@sentry/types";
import Configuration from "./config/config";

// Install the vue plugin
Vue.use(VueApollo);

// Name of the localStorage item
const AUTH_TOKEN = Configuration.value("VUE_APP_APOLLO_AUTH_TOKEN") || "apollo-token";

// Http endpoint
const httpEndpoint = Configuration.value("VUE_APP_GRAPHQL_HTTP") || "http://localhost:4000/graphql";
// Files URL root
export const filesRoot = Configuration.value("VUE_APP_FILES_ROOT") || httpEndpoint.substr(0, httpEndpoint.indexOf("/graphql"));

Vue.prototype.$filesRoot = filesRoot;

// Bearer token for authorization
const authBearer = Buffer.from(AUTH_TOKEN, "base64").toString("ascii");
const authMiddleware = new ApolloLink((operation, forward) => {
    // add the authorization to the headers
    operation.setContext({
        headers: {
            authorization: `Bearer ${authBearer}` || null,
        }
    });

    return forward && <any>forward(operation); //@todo fix interface (<any>)
});


// Config
const defaultOptions = {
    // You can use `https` for secure connection (recommended in production)
    httpEndpoint,
    // You can use `wss` for secure connection (recommended in production)
    // Use `null` to disable subscriptions
    // wsEndpoint: Configuration.value("VUE_APP_GRAPHQL_WS") || "ws://localhost:4000/graphql",
    // LocalStorage token
    tokenName: AUTH_TOKEN,
    // Enable Automatic Query persisting with Apollo Engine
    persisting: false,
    // Use websockets for everything (no HTTP)
    // You need to pass a `wsEndpoint` for this to work
    // websocketsOnly: true,
    // Is being rendered on the server?
    ssr: false,

    // Override default apollo link
    // note: don't override httpLink here, specify httpLink options in the
    // httpLinkOptions property of defaultOptions.
    link: authMiddleware,

    // Override default cache
    cache: new InMemoryCache()

    // Override the way the Authorization header is set
    // getAuth: (tokenName) => ...

    // Additional ApolloClient options
    // apollo: { ... }

    // Client local data (see apollo-link-state)
    // clientState: { resolvers: { ... }, defaults: { ... } }
};

/**
 * Call this in the Vue app file
 * @param {Object} options
 * @param {Object} config
 * @returns {VueApollo}
 */
export function createProvider(options: any = {}, config: any = {}) {
    // Create apollo client
    const { apolloClient, wsClient } = createApolloClient({
        ...defaultOptions,
        ...options
    });

    if (wsClient && wsClient.connectionParams) {
        const transactionId = Math.random().toString(36).substr(2, 9);

        if (config.errorLogger) {
            /* istanbul ignore next */
            config.errorLogger.configureScope((scope: Scope) => {
                scope.setTag("transaction_id", transactionId);
            });
        }

        wsClient.connectionParams = () => {
            return {
                headers: {
                    "Authorization": authBearer ? `Bearer ${authBearer}` : null,
                    "X-Transaction-ID": transactionId,
                    "X-Forwarded-For": ""
                }
            };
        };
    }

    apolloClient.wsClient = wsClient;

    // Create vue apollo provider
    return new VueApollo({
        defaultClient: apolloClient,
        defaultOptions: {
            $query: {
                fetchPolicy: "no-cache",
            },
        },
        errorHandler(error) {
            /* istanbul ignore next */
            // eslint-disable-next-line no-console
            console.log("%cError", "background: red; color: white; padding: 2px 4px; border-radius: 3px; font-weight: bold;", error.message);
        },
    });
}

/**
 * Manually call this when user log in
 * @param {*} apolloClient
 * @param {*} token
 * @returns {Promise<void>}
 */
export async function onLogin(apolloClient: { apolloClient: ApolloClient<any>, wsClient: SubscriptionClient }, token: string) {
    if (typeof localStorage !== "undefined" && token) {
        localStorage.setItem(AUTH_TOKEN, token);
    }
    if (apolloClient.wsClient) restartWebsockets(apolloClient.wsClient);
    try {
        await apolloClient.apolloClient.resetStore();
    } catch (e) {
        /* istanbul ignore next */
        // eslint-disable-next-line no-console
        console.log("%cError on cache reset (login)", "color: orange;", e.message);
    }
}

/**
 * Manually call this when user log out
 * @param {*} apolloClient
 * @returns {Promise<void>}
 */
export async function onLogout(apolloClient: { apolloClient: ApolloClient<any>, wsClient: SubscriptionClient }) {
    if (typeof localStorage !== "undefined") {
        localStorage.removeItem(AUTH_TOKEN);
    }
    if (apolloClient.wsClient) restartWebsockets(apolloClient.wsClient);
    try {
        await apolloClient.apolloClient.resetStore();
    } catch (e) {
        /* istanbul ignore next */
        // eslint-disable-next-line no-console
        console.log("%cError on cache reset (logout)", "color: orange;", e.message);
    }
}
